import { useEffect, useMemo, useState } from 'react';
import {
  AnemiaProtocolStatus,
  PreopLabTestProtocolStatus,
  ProtocolName,
  ProtocolStatuses,
  useAvailableStatusTransitions,
  useTriggerAnemiaCopilot,
  useUpdateProtocol
} from '@/modules/protocol';
import {
  usePrevious,
  useRouterPush,
  useTranslator
} from '@ianesthesie-kit/core';
import {
  openProtocolDrawer,
  toProtocolStatuses,
  TriageGroup
} from '@/modules/appContext';
import { useAppDispatch } from '@/hooks';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import { PatientDetailsCardId } from '@/pages-client/patientDetails/components/patientDetailsCardId';
import { Patient } from '@/modules/surgicalEvent';
import {
  AnimatedChipProps,
  ExtraActions
} from '@/components/protocolStatusActionButton';
import { LAB_ORDER_DRAWER_PROTOCOL_MUTATION_CACHE_KEY } from '@/config';
import { doctorBffApi } from '@/modules';

type UseAnemiaExtraActionsProps = {
  patient: Patient;
  statuses: ProtocolStatuses | undefined;
  copilotAvailable: boolean | undefined;
};

function isLabOrderAvailable(
  anemiaProtocolStatus: AnemiaProtocolStatus | undefined
) {
  return (
    AnemiaProtocolStatus.AUTO_NO_HB_DETECTED === anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_POTENTIALLY_ANEMIC === anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_POTENTIALLY_NOT_ANEMIC === anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_ANEMIA_NOT_DETECTED === anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_ANEMIA_DETECTED === anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_IRON_INJECTION_PRESCRIPTION_RECOMMENDED ===
      anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_EPO_AND_IRON_INJECTION_PRESCRIPTION_RECOMMENDED ===
      anemiaProtocolStatus ||
    AnemiaProtocolStatus.AUTO_EXPERT_REFERRAL_RECOMMENDED ===
      anemiaProtocolStatus ||
    AnemiaProtocolStatus.EXTRA_LAB_TESTS_REQUESTED === anemiaProtocolStatus ||
    AnemiaProtocolStatus.IRON_INJECTIONS_DONE === anemiaProtocolStatus
  );
}

export const useAnemiaExtraActions = ({
  statuses,
  patient,
  copilotAvailable
}: UseAnemiaExtraActionsProps): AnimatedChipProps[] | undefined => {
  const push = useRouterPush();
  const translate = useTranslator();
  const dispatch = useAppDispatch();
  const [triggerCopilot, { isLoading }] = useTriggerAnemiaCopilot();
  const [labTestRequestAttempted, setLabTestRequestAttempted] = useState(false);
  const [updateProtocol, { isLoading: isSubmitting }] = useUpdateProtocol();
  const { showErrorDialog } = useErrorDialog();
  const { availableTransitions: manualTransitions } =
    useAvailableStatusTransitions(
      ProtocolName.ANEMIA,
      AnemiaProtocolStatus.AUTO_ANEMIA_DETECTED
    );

  const [
    ,
    {
      status: preopLabTestMutationStatus,
      fulfilledTimeStamp: preopLabTestMutationSFulfilledAt
    }
  ] = useUpdateProtocol({
    fixedCacheKey: LAB_ORDER_DRAWER_PROTOCOL_MUTATION_CACHE_KEY
  });
  const lastPreopLabTestMutationSFulfilledAt = usePrevious(
    preopLabTestMutationSFulfilledAt
  );

  // TODO this orchestration should be part of backend logic
  useEffect(() => {
    if (
      preopLabTestMutationStatus !== 'fulfilled' ||
      !labTestRequestAttempted
    ) {
      return;
    }

    const isNewMutation =
      !preopLabTestMutationSFulfilledAt ||
      preopLabTestMutationSFulfilledAt !== lastPreopLabTestMutationSFulfilledAt;

    if (!isNewMutation) {
      return;
    }

    updateProtocol({
      protocol: ProtocolName.ANEMIA,
      targetStatus: AnemiaProtocolStatus.EXTRA_LAB_TESTS_REQUESTED,
      patientId: patient.id
    })
      .unwrap()
      .then(() => {
        // some race condition (probably) messes with that
        setTimeout(() => {
          dispatch(doctorBffApi.util?.invalidateTags(['Dashboard']));
        }, 200);
      })
      .catch((err) => {
        //TODO handle sentry
        showErrorDialog(err);
      })
      .finally(() => {
        setLabTestRequestAttempted(false);
      });
  }, [
    dispatch,
    labTestRequestAttempted,
    lastPreopLabTestMutationSFulfilledAt,
    preopLabTestMutationSFulfilledAt,
    preopLabTestMutationStatus,
    patient.id,
    showErrorDialog,
    updateProtocol
  ]);

  return useMemo(() => {
    const goToAnchor = (cardId: PatientDetailsCardId) => {
      push(`/patients/${patient.id}?card=${cardId}`);
    };
    const anemiaStatus = statuses?.anemiaProtocolStatus;
    const actions: ExtraActions = [];
    if (copilotAvailable) {
      actions.push({
        key: 'copilot',
        textId: 'doctor-dashboard.protocol.anemia.triggerCopilot',
        loading: isLoading,
        clickable: !isLoading,
        onClick: () =>
          triggerCopilot({
            patientId: patient.id
          })
      });
    }
    if (anemiaStatus === AnemiaProtocolStatus.AUTO_POTENTIALLY_ANEMIC) {
      actions.push({
        key: 'gotoLabTests',
        textId: 'doctor-dashboard.protocol.anemia.goToDetailsLabTest',
        variant: 'outlined' as const,
        onClick: () => goToAnchor(PatientDetailsCardId.LAB_TESTS)
      });
    }

    if (anemiaStatus === AnemiaProtocolStatus.AUTO_NO_HB_DETECTED) {
      actions.push({
        key: 'gotoLabOrders',
        textId: 'doctor-dashboard.protocol.anemia.goToDetailsLabOrder',
        variant: 'outlined' as const,
        onClick: () => goToAnchor(PatientDetailsCardId.LAB_ORDER)
      });
    }

    if (isLabOrderAvailable(statuses?.anemiaProtocolStatus)) {
      const onClick = () => {
        dispatch(
          openProtocolDrawer({
            type: ProtocolName.PREOP_LAB_TEST,
            data: {
              patient: patient,
              targetStatus:
                PreopLabTestProtocolStatus.PREOPERATIVE_LAB_TEST_PRESCRIPTION_SENT
            }
          })
        );
        setLabTestRequestAttempted(true);
      };
      actions.push({
        key: 'sendLabOrder',
        textId:
          'doctor-dashboard.protocol.status.action.to.EXTRA_LAB_TESTS_REQUESTED',
        variant: 'outlined' as const,
        loading: isSubmitting,
        onClick
      });
    }

    const toCheckStatuses = toProtocolStatuses(
      'TO_CHECK',
      TriageGroup.PREOP_ANEMIA
    );
    const isOnToCheck =
      anemiaStatus &&
      Array.isArray(toCheckStatuses) &&
      toCheckStatuses.includes(anemiaStatus);

    if (!copilotAvailable && isOnToCheck) {
      actions.push({
        key: 'anemiaManualActions',
        text: translate('doctor-dashboard.protocol.anemia.manualActions'),
        actions: manualTransitions,
        manualOverride: true
      });
    }

    return actions;
  }, [
    copilotAvailable,
    statuses?.anemiaProtocolStatus,
    patient,
    push,
    isLoading,
    triggerCopilot,
    isSubmitting,
    dispatch,
    translate,
    manualTransitions
  ]);
};
